import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  currentShapefileFeatureProperty: undefined,
  currentShapefileIdForFeatureProperty: undefined,
  currentShapefileFeatureSortDirection: undefined,
  /**
   * Indicates whether initial values of the project and it's dependences have been set.
   * For more implementation details, see handling in the [useProjectInitialization hook](../../../components/project/projects/hooks/useProjectInitialization/useProjectInitialization.tsx)
   */
  hasProjectInitialized: false,
  hasViewItemsChanged: false,
  projectTree: [],
  selectedFeatureProperty: {},
  selectedProjectTransaction: "",
  selectedShapefile: null,
  shapefileProperties: {},
  shapefileFeatures: {},
  isTogglingMidstream: false
};

const createRandomTransactionId = () => {
  const length = 5;
  const charset = "abcdefghijklmnopqrstuvwxyz";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += charset[Math.floor(Math.random() * charset.length)];
  }
  return result;
};
const projectSlice = createSlice({
  name: "projectSlice",
  initialState,
  reducers: {
    setProjectTree(state, action) {
      state.projectTree = action.payload;
    },
    setHasProjectInitialized(state, action) {
      state.hasProjectInitialized = action.payload;
    },
    setHasViewItemsChanged(state, action) {
      state.hasViewItemsChanged = action.payload;
    },
    setSelectedShapefile(state, action) {
      const shapefile = action.payload;
      state.selectedShapefile = shapefile;

      if (shapefile?.shapefileId && shapefile?.properties?.length > 0) {
        const id = shapefile.projectShapefileId || shapefile.shapefileId;

        if (!state.selectedFeatureProperty[id]) {
          const property =
            shapefile.properties.find((x) => x === "NAME") ?? shapefile.properties[0];

          if (property) {
            state.selectedFeatureProperty[id] = property;
            state.currentShapefileFeatureProperty = property;
            state.currentShapefileIdForFeatureProperty = id;
          }
        }
      }
    },
    setShapefileProperties(state, action) {
      const { shapefileKey, propertyKey, details: newDetails } = action.payload;
      if (!shapefileKey || !propertyKey) return;

      const current = state.shapefileProperties?.[shapefileKey] ?? {};
      const existingDetails = current[propertyKey] ?? {};

      state.shapefileProperties = {
        ...state.shapefileProperties,
        [shapefileKey]: {
          ...current,
          [propertyKey]: {
            ...existingDetails,
            ...newDetails
          }
        }
      };
    },
    setShapefileFeatures(state, action) {
      state.shapefileFeatures = action.payload;
    },
    setCurrentShapefileIdForFeatureProperty(state, action) {
      state.currentShapefileIdForFeatureProperty = action.payload;
    },
    setCurrentShapefileFeatureProperty(state, action) {
      state.currentShapefileFeatureProperty = action.payload;
    },
    setCurrentShapefileFeatureSortDirection(state, action) {
      state.currentShapefileFeatureSortDirection = action.payload;
    },
    // TODO BF: rename, and keep, will be useful if we use multiple shapefile properties
    // Stores all the selected feature properties for each shapefile
    setSelectedFeatureProperty(state, action) {
      state.selectedFeatureProperty[state.selectedShapefile?.key] = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateSelectedProjectTransaction(state, action) {
      state.selectedProjectTransaction = createRandomTransactionId();
    },
    setIsTogglingMidstream(state, action) {
      state.isTogglingMidstream = action.payload;
    },
    resetProjectSlice: () => initialState
  }
});

export const {
  setCurrentShapefileIdForFeatureProperty,
  setCurrentShapefileFeatureProperty,
  setCurrentShapefileFeatureSortDirection,
  setProjectTree,
  setHasProjectInitialized,
  setHasViewItemsChanged,
  setSelectedFeatureProperty,
  setSelectedShapefile,
  setShapefileProperties,
  setShapefileFeatures,
  updateSelectedProjectTransaction,
  setIsTogglingMidstream,
  resetProjectSlice
} = projectSlice.actions;

export default projectSlice.reducer;
